import { v4 } from "uuid"
import { AccessRights, USER_ROLE } from "./user"
import { ACTION_TYPE, addIndexedValues, Allocations, CARBON_SCOPE, createIndexedValues, CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, FILTER_MODEL_KEY, GraphData, IndexedValues, INSIGHTS_FIELD, INSIGHTS_SECTION, isModelNode, isOrganisationNode, LinqsetNode, ModelValueContributions, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, roundAllocations, roundIndexedValues, sumAllocations, TUTORIAL_MODEL_KEY } from "./linqset-base"

export { ACTION_TYPE, CARBON_SCOPE, type ModelValueContributions } from "./linqset-base"

export const FREE_NODES = 150
export const FREE_PROJECTS = 1
export const FREE_USERS = 1

export const MODEL_TAG_GROUP_KEY = "modeltags"
export const MODEL_NAME_MIN = 3
export const MODEL_NAME_MAX = 50
export const MODEL_DESCRIPTION_MAX = 200

export const PROJECT_NAME_MIN = 3
export const PROJECT_NAME_MAX = 50
export const PROJECT_DESCRIPTION_MAX = 100

export const ORGANISATION_NAME_MIN = 3
export const ORGANISATION_NAME_MAX = 40

export const ORGANISATION_ALMOST_EXPIRED = 14

export const MAX_BACKUPS = 5

export const MODEL_EXPIRY_DAYS = 30

export const EMISSION_FACTOR_NAME_MIN = 3
export const EMISSION_FACTOR_NAME_MAX = 40
export const EMISSION_FACTOR_DESCRIPTION_MAX = 150
// kg eCO2 per unit; high enough for per tonne materials such as aluminium, and totals for items with no usage
export const EMISSION_FACTOR_VALUE_MAX = 1000000

export const TAG_GROUP_ALL_MODELS = "all-models"

export const DEFAULT_CURRENCY = "NZD"

export const roundTwoDP = (num:number) => {
    return Math.round(num*100)/100
}

type EnumType = { [key: string]: string | number };
type EnumAsArrayType = {
  key: string;
  value: string | number;
}[];

export const enumToArray = (data: EnumType): EnumAsArrayType =>
    Object.keys(data)
      .filter((key) => Number.isNaN(+key))
      .map((key: string) => ({
        key,
        value: data[key],
}));

export const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export const getCarbonScopeDescription = (scope:CARBON_SCOPE) => {
    switch(scope) {
        case CARBON_SCOPE.ScopeOne:
            return "Emissions from sources that an organisation owns or controls directly"
        case CARBON_SCOPE.ScopeTwo:
            return "Emissions that a company causes indirectly and come from where the energy it purchases and uses is produced"
        case CARBON_SCOPE.ScopeThree:
            return "Emissions that are not produced by the company itself and are not the result of activities from assets owned or controlled by them, but by those that it’s indirectly responsible for up and down its value chain"
    }
}

export const getCarbonScopeShortDescription = (scope:CARBON_SCOPE) => {
    switch(scope) {
        case CARBON_SCOPE.ScopeOne:
            return "Direct emissions"
        case CARBON_SCOPE.ScopeTwo:
            return "Purchased energy emissions"
        case CARBON_SCOPE.ScopeThree:
            return "Other indirect / value chain emissions"
    }
}

export const getCarbonScopeIcon = (scope:CARBON_SCOPE) => {
    switch(scope) {
        case CARBON_SCOPE.ScopeOne:
            return "factory"
        case CARBON_SCOPE.ScopeTwo:
            return "power"
        case CARBON_SCOPE.ScopeThree:
            return "local_shipping"
    }
}

export enum EMISSION_FACTOR_TYPE {
    VOLUME = "volume",
    WEIGHT = "weight",
    POWER = "power",
    DISTANCE = "transport",
    ENERGY = "energy",
    NO_USAGE = "no usage",
}

export const getUnitsForEmissionFactor = (ef:EMISSION_FACTOR_TYPE):string => {
    switch (ef) {
        case EMISSION_FACTOR_TYPE.VOLUME:
            return 'litre'
        case EMISSION_FACTOR_TYPE.WEIGHT:
            return 'tonne'
        case EMISSION_FACTOR_TYPE.POWER:
            return 'kWh'
        case EMISSION_FACTOR_TYPE.DISTANCE:
            return 'tonnes.km'
        case EMISSION_FACTOR_TYPE.ENERGY:
            return 'litre'
        case EMISSION_FACTOR_TYPE.NO_USAGE:
            return ''
    }
}

export const getEmissionFactorDescription = (ef:EMISSION_FACTOR_TYPE):string => {
    switch (ef) {
        case EMISSION_FACTOR_TYPE.VOLUME:
            return 'Emissions from consuming/burning a liquid, such as diesel/petrol. Must know amount in litres.'
        case EMISSION_FACTOR_TYPE.WEIGHT:
            return 'Emissions from consuming/burning a solid, such as coal. Must know weight in tonnes.'
        case EMISSION_FACTOR_TYPE.POWER:
            return 'Emissions from using electricity. Must know kwh used.'
        case EMISSION_FACTOR_TYPE.DISTANCE:
            return 'Emissions from transporting something. Must know weight and distance.'
        case EMISSION_FACTOR_TYPE.ENERGY:
            return 'Emissions from consuming/burning a liquid. Calculated using raw energy parameters.'
        case EMISSION_FACTOR_TYPE.NO_USAGE:
            return 'Factor used as total emissions, so no usage entered. Emissions can then be calculated per person, lifespan or unit, e.g. manufacturing emissions from each persons laptop.'
    }
}

export enum MODEL_FUNCTION {
    Accounts = "Accounts",
    Administration = "Administration",
    Analysis = "Analysis",
    AssetManagement = "Asset management",
    CallCentre = "Call centre",
    Communications = "Communications",
    Core = "Core",
    CustomerService = "Customer service",
    ECommerce = "E-commerce",
    Financial = "Financial",
    HealthSafety = "Health and safety",
    HumanResources = "HR",
    Invoicing = "Invoicing",
    Management = "Management",
    Manufacturing = "Manufacturing",
    Marketing = "Marketing",
    Medical = "Medical",
    Military = "Military",
    Onboarding = "Onboarding",
    Operational = "Operational",
    Payroll = "Payroll",
    Planning = "Planning",
    PriceQuoting = "Price quoting",
    Processing = "Processing",
    Procurement = "Procurement",
    ProductDevelopment = "Product development",
    Recruitment = "Recruitment",
    Reporting = "Reporting",
    Research = "Research",
    Sales = "Sales",
    SoftwareDevelopment = "Software development",
    Support = "Support",
    Training = "Training",
    Transportation = "Transportation",
    Other = "Other"
}

export enum INDUSTRY {
    Accounting = "Accounting",
    Aerospace = "Aerospace",
    Aeroplanes = "Aeroplanes",
    Agriculture = "Agriculture",
    AutomobileSales = "Automobile sales",
    AutomobileManufacturing = "Automobile manufacturing",
    Bank = "Bank",
    BusinessConsulting = "Business consulting",
    BusinessServices = "Business services",
    BusinessSoftware = "Business software",
    Charity = "Charity",
    Clothing = "Clothing",
    Communications = "Communications/PR",
    Construction = "Construction",
    ConsumerElectronics = "Consumer electronics",
    ConsumerServices = "Consumer services",
    ConsumerSoftware = "Consumer software",
    Council = "Council",
    ECommerce = "E-commerce",
    Education = "Education",
    Fashion = "Fashion",
    Financial = "Financial",
    FoodServices = "Food services",
    Forestry = "Forestry",
    Government = "Government",
    Health = "Health",
    Hospitality = "Hospitality",
    Hotel = "Hotal",
    Insurance = "Insurance",
    ITConsulting = "IT consulting",
    Law = "Law",
    Manufacturing = "Manufacturing",
    Media = "Media",
    Medical = "Medical",
    Military = "Military",
    Mining = "Mining",
    Movies = "Movies",
    Ports = "Port operations",
    Retail = "Retail",
    RealEstate = "Real estate",
    SAAS = "Software as a service",
    Science = "Science",
    Television = "Television",
    Trade = "Trade",
    Transportation = "Transportation",
    Utilities = "Utilities",
    Other = "Other"
}

export enum FUNCTIONS {
    COSTS = "costs",
    VALUE = "value",
    CARBON = "carbon",
    CAPACITY = "capacity",
    TIME = "time",
    LOAD = "load"
}

export enum ORGANISATION_STATE {
    EXPIRED = "Expired",
    NEAR_EXPIRY = "Near expiry",
    ACTIVE = "Active"
}

export enum ORGANISATION_STAGE {
    REGISTERED,
    FIRST_MODEL_DATA,
    FIRST_PAYMENT
}

export const getActionTypeName = (type:ACTION_TYPE) => {
    switch(type) {
        case ACTION_TYPE.NONE:
            return "No role or system"
        case ACTION_TYPE.AUTOMATED:
            return "Automated"
        case ACTION_TYPE.MANUAL:
            return "Manual"
        case ACTION_TYPE.BOTH:
            return "Role using system"
        case ACTION_TYPE.EXTERNAL:
            return "External"
    }
}

export enum MODEL_CHANGE {
    OTHER,
    UPDATE_MODEL,
    UPLOAD_GRAPH,
    REPLACE_CURRENT_STATE,
    DELETE_MODEL,
    CREATE_PROJECT,
    DELETE_PROJECT,
    BACKUP_MODEL,
    RESTORE_MODEL,
    DELETE_BACKUP,
    CLOSE_ORGANISATION,
    REMOVE_USER,
    ADD_USER,
    BACKUP_ORG,
    RESTORE_ORG_BACKUP,
    DELETE_ORG_BACKUP,
    COPY_MODEL
}

export class Currency {
    constructor(public code:string, public symbol:string, public name:string, public node:number, public project:number, public user:number){}

    get userPrice() {
        return `${this.symbol}${this.user} ${this.code}`
    }

    get projectPrice() {
        return `${this.symbol}${this.project} ${this.code}`
    }

    get nodePrice() {
        return `${roundTwoDP(this.node*100)} cents`
    }
}

export const Currencies:Currency[] = [
    new Currency('USD', '$', 'US dollar', .15, 60, 15),
    new Currency('NZD', '$', 'NZ dollar', .25, 100, 25),
    new Currency('AUD', '$', 'Australian dollar', .24, 94, 24),
    new Currency('EUR', '\u20AC', 'Euro', .14, 53, 14),
    new Currency('GBP', '\u00A3', 'British pound', .12, 45, 12)
]

export interface AIPrompt {
    name:string
    comparison:boolean
    description:string
    prompt:string
}

export interface ModelSummary {
    _key:string
    name:string
    prompt:string
    compare:boolean         // whether is comparing project with current state
    created:Date
    organisationKey:string
    modelKey:string
    projectKey:string
    filterKey?:string
    htmlSummary:string
}

export const createModelSummary = (organisationKey:string, name:string, prompt:string, htmlSummary:string, modelKey:string, workspaceKey:string, filterKey?:string):ModelSummary => {
    return {
        _key:v4(),
        name,
        prompt,
        organisationKey,
        modelKey,
        projectKey: workspaceKey,
        filterKey,
        htmlSummary
    } as ModelSummary
}

export const updateModelSummary = (summary:ModelSummary, name:string, prompt:string, htmlSummary:string):ModelSummary => {
    summary.name = name
    summary.prompt = prompt
    summary.htmlSummary = htmlSummary
    return summary
}

export interface ModelAnalytics {
    _key:string
    type:MODEL_CHANGE,
    organisationKey:string
    modelKey:string
    projectKey:string
    modelName: string
    userKey:string
    userName:string
    nodeChanges: number
    edgeChanges: number
    deletedNodes: number
    deletedEdges: number
    newNodes: number
    newEdges: number
    createdDate:Date,
    nodes:number,           // number of organisation current state nodes at this time
    projectNodes:number,    // number of organisation project nodes at this time
    backupNodes:number,     // number or organisation backup nodes at this time
    projects:number,        // number of projects
    users:number            // number of users
}

export interface InsightTotal {
    number:number // number of items
    allocatedCost:number
    hours:number
    percentageModelled:number
    capacity:number
}

export const createInsightTotal = ():InsightTotal => {
    return {number:0, allocatedCost:0, hours:0, percentageModelled:0, capacity:0} as InsightTotal
}

export const addInsightTotal = (from:InsightTotal, to:InsightTotal, sType:NODE_SUPERTYPE) => {
    if (isModelNode(sType)) to.number += from.number

    to.allocatedCost += from.allocatedCost
    to.hours += from.hours
    to.capacity += from.capacity
}

export interface ActionType {
    number:number,
    processed:number,
    cost:number,
    hours:number
}

export const createActionType = ():ActionType => {
    return {number:0, cost:0, hours:0, processed:0} as ActionType
}

export const getTotalModelCost = (insights:ModelInsights) => {
    if (!insights?.superTypes) return 0
    if (insights.modelKey === ORGANISATION_MODEL_KEY) {
        const total = sumAllocations(insights.costAllocations)
        return total.allocated + total.unallocated
    }else return insights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].allocatedCost + insights.superTypes[NODE_SUPERTYPE.PEOPLE].allocatedCost + insights.superTypes[NODE_SUPERTYPE.ROLE].allocatedCost + insights.superTypes[NODE_SUPERTYPE.SYSTEM].allocatedCost
}

export const getPeopleRoleModelCost = (insights:ModelInsights) => {
    if (!insights?.superTypes) return 0
    if (insights.modelKey === ORGANISATION_MODEL_KEY) {
        const total = sumAllocations(insights.costAllocations.filter(a => a.superType === NODE_SUPERTYPE.PEOPLE || a.superType === NODE_SUPERTYPE.ROLE))
        return total.allocated + total.unallocated
    }else return (insights.superTypes[NODE_SUPERTYPE.PEOPLE]?.allocatedCost||0) + (insights.superTypes[NODE_SUPERTYPE.ROLE]?.allocatedCost||0)
}

export const getSystemInfrastructureModelCost = (insights:ModelInsights) => {
    if (!insights?.superTypes) return 0
    if (insights.modelKey === ORGANISATION_MODEL_KEY) {
        const total = sumAllocations(insights.costAllocations.filter(a => a.superType === NODE_SUPERTYPE.INFRASTRUCTURE || a.superType === NODE_SUPERTYPE.SYSTEM))
        return total.allocated + total.unallocated
    }else return insights.superTypes[NODE_SUPERTYPE.INFRASTRUCTURE].allocatedCost + insights.superTypes[NODE_SUPERTYPE.SYSTEM].allocatedCost
}

export const getCostsForType = (insights:ModelInsights, superType:NODE_SUPERTYPE) => {
    if (!insights?.superTypes) return undefined
    if (insights.modelKey === ORGANISATION_MODEL_KEY) {
        const allocation = insights.costAllocations.find(a => a.superType === superType)
        return allocation?(allocation.allocated + allocation.unallocated):0
    }else return insights.superTypes[superType].allocatedCost
}

export interface ModelInsights {
    _key: string,
    organisationKey:string,
    modelKey:string,
    projectKey:string // current state key or project key

    updatedDate:Date

    numModels:number // number of models in org workspace (used for org or project model insights only)

    superTypes:any  // InsightTotal indexed by node super type
    externalOrgs:InsightTotal        // totals for external organisations so can report on them separately

    emissions:Allocations[]             // Emissions by scope
    costAllocations:Allocations[]       // costs generated from this type. only applies at org level

    actionTypes: any // ActionTypes indexed by ACTION_TYPE
    allocatedCarbonByScope:IndexedValues            // carbon emissions indexed by scope

    informationAssets: NodeGroupModelInsights[]
    tags: NodeGroupModelInsights[]

    modelledCapacity:number             // total role capacity in a model
    unModelledCapacity:number             // unmodelled role capacity for this model
    unusedCapacity:number               // role capacity that are not in any models
    infoSuppliedBySystems:number
    outputValue:number                  // this is the sum of the value of each output multiplied by number of that outputs itmes processed
    outputRevenue:number                // this is the sum of the revenue of each output multiplied by number of that outputs itmes processed
    valueFromModels?:ModelValueContributions    // part of outputValue/outputRevenue received from other (downstream) models via shared outputs
    frictionCost:number                 // cost of all outputs/outcomes that have zero value or revenue
    frictionCarbon:number               // carbon emissions for all outputs/outcomes that have zero value or revenue
    frictionTime:number                 // time spent for all outputs/outcomes that have zero value or revenue
    // exception work: actions on a detour from the main flow (or marked as exception work), which is friction too
    exceptionCost:number
    exceptionCarbon:number
    exceptionTime:number
    exceptionActions:number
    // other waste: actions marked as not adding value, which are friction too
    wasteCost?:number
    wasteTime?:number

    numOutputs:number   // number of information output nodes
    numOutputsProcessed:number // number of information output items processed
    numInputsProcessed:number   // number of information inputs processed
    numInputsExternal:number   // number of information inputs coming externally into org

    // whether there were any balancing errors
    processingErrors: string[]
}

// represents backup data. Uses version number to identify nodes and edges
export interface Backup {
    _key:string,
    projectKey:string
    date:Date
    serverVersion:string
    description:string
    user?:string
    nodeCount:number,
    edgeCount:number
}

export interface ModelChanges {
    projectKey:string
    userKey:string
    date:Date
}

export interface ModelDetails {
    _key: string,
    function: MODEL_FUNCTION,
    isLibrary:boolean   // whether model is a library model (ie. only exists as part of another model)
    projects:string[] // list of project keys that have changed this model from current state
    projectKey: string, // is current state key or if is created in a project the projects key
    name: string,
    description: string,
    useOrganisationAccess:boolean,      // whether access is based on organisation access rules
    insights: ModelInsights[] // insights for model current state and any projects it appears in
    userAccess:AccessRights[]
    tagKeys:string[]        // model tags that have been applied to model

    backups:Backup[]

    owner:string        // user key of owner of model.
    createdDate:Date
    updatedDate:Date,
    changes:ModelChanges[],         // last change made to the model and the date it happened
    reviewDate:Date|undefined,    // date set by user when model requires review
    deletedDate:Date|undefined, // date this model was deleted. Will only be shown if showing deleted models. Is permanently delted after 30 days
    // current users role. is only used to return data to client, is not stored
    role:USER_ROLE|undefined
}

export const summariseModel = (model:ModelDetails, projectKey:string) => {
    const insights = model.insights.find(i => i.projectKey === projectKey)

    return {
        function:model.function,
        name:model.name,
        description: model.description,
        insights
    }
}

export const getProjectName = (organisation:Organisation, projectKey:string) => {
    if (projectKey === CURRENT_STATE_PROJECT_KEY) return "Current state"
    const project = organisation.projects?.find(p => p._key === projectKey)
    if (project) return project.name
    return ""
}

export const createTutorialModel = () => {
    const model = createModelDetails({name:"Tutorial", userAccess:[]}, TUTORIAL_MODEL_KEY)
    model.role = USER_ROLE.WRITE
    model.projectKey = CURRENT_STATE_PROJECT_KEY
    return model
}

export const createFilterModel = (workspaceKey:string) => {
    const model = createModelDetails({name:"Filter", userAccess:[]}, FILTER_MODEL_KEY, workspaceKey)
    model.role = USER_ROLE.READ
    return model
}

export const createModelDetails = (modelData?:any, key?:string, workspaceKey?:string) => {
    const now = new Date()
    const newModel = {
        _key: key || v4(),
        projectKey: modelData?.projectKey||workspaceKey||CURRENT_STATE_PROJECT_KEY,
        useOrganisationAccess: true,
        insights: [],
        tagKeys: [],
        projects: [],
        createdDate: now,
    } as unknown as ModelDetails
    if (newModel.projectKey !== CURRENT_STATE_PROJECT_KEY)
         newModel.projects.push(newModel.projectKey)

    if (modelData) updateModelData(newModel, modelData)
    return newModel
}

export const updateModelData = (existingModel:ModelDetails, modelData:ModelDetails) => {
    if (!modelData.name || modelData.name.length < MODEL_NAME_MIN || modelData.name.length > MODEL_NAME_MAX) throw Error(`Model name must be between ${MODEL_NAME_MIN} and ${MODEL_NAME_MAX} characters`)
    if (modelData.description && modelData.description.length > MODEL_DESCRIPTION_MAX) throw Error(`Model description must be less than ${MODEL_DESCRIPTION_MAX} characters`)

    existingModel.function = modelData.function || existingModel.function
    existingModel.name = modelData.name
    existingModel.description = modelData.description||""
    existingModel.useOrganisationAccess = modelData.useOrganisationAccess
    existingModel.tagKeys = modelData.tagKeys?modelData.tagKeys:existingModel.tagKeys
    existingModel.updatedDate = new Date()
    existingModel.reviewDate = modelData.reviewDate || existingModel.reviewDate
    existingModel.owner = modelData.owner || existingModel.owner

    if (modelData.userAccess && modelData.userAccess.length>0) existingModel.userAccess = modelData.userAccess

    if (existingModel._key !== TUTORIAL_MODEL_KEY && existingModel._key !== FILTER_MODEL_KEY && !existingModel.useOrganisationAccess && !existingModel.userAccess.find(a => a.access === USER_ROLE.ADMIN)) throw Error("Model must have an administrator")
}

export const canDeleteModel = (organisation:Organisation, model:ModelDetails, project?:Project) => {
    const canEdit = model.role && model.role === USER_ROLE.ADMIN
    if (project && modelChangedInProject(model, project._key) && project.role === USER_ROLE.ADMIN && canEdit) return true
    return organisation.role === USER_ROLE.ADMIN && canEdit
}

export interface Project {
    _key: string,
    name:string,
    description:string,
    sandbox:boolean,
    startDate:Date,
    completionDate?:Date,
    useOrganisationAccess:boolean,      // whether access is based on organisation access rules
    userAccess:AccessRights[],
    // current users role. is only used to return data to client, is not stored
    role:USER_ROLE|undefined
}

// the projects that are billed: all of them
export const billableProjectCount = (projects:Project[]|undefined) => (projects || []).length

// the users that are billed: those who've accepted their invitation. Access given before invitations has no activated flag
export const billableUserCount = (users:AccessRights[]|undefined) => (users || []).filter(u => u.activated !== false).length

export const createProject = (key?:string) => {
    const now = new Date()
    const k = key||v4()
    return {_key:k, name:"", description:"", sandbox: false, userAccess:[], useOrganisationAccess:true, startDate:now, completionDate:undefined, role:undefined} as Project
}

export const isProject = (value: Project): value is Project => {
    if (value._key && value.startDate)
        return true
    else
        return false;
}

// a project using organisation access is administered by the organisation's administrators; otherwise it needs one
// of its own, as a model does
export const updateProjectData = (project:Project, projectData:Project) => {
    const name = projectData.name || ''
    const description = projectData.description || ''
    if (name.length < PROJECT_NAME_MIN || name.length > PROJECT_NAME_MAX) throw Error(`Project name must be between ${PROJECT_NAME_MIN} and ${PROJECT_NAME_MAX} characters`)
    if (description.length > PROJECT_DESCRIPTION_MAX) throw Error(`Project description must be less than ${PROJECT_DESCRIPTION_MAX} characters`)
    if (!projectData.useOrganisationAccess && !(projectData.userAccess || []).find(a => a.access === USER_ROLE.ADMIN)) throw Error("Project must have an administrator")

    project.name = name
    project.description = description
    project.userAccess = projectData.userAccess || []
    project.useOrganisationAccess = projectData.useOrganisationAccess
    project.startDate = projectData.startDate
    project.completionDate = projectData.completionDate
}

export const modelChangedInProject = (model:ModelDetails, workspaceKey:string) => {
    return workspaceKey !== CURRENT_STATE_PROJECT_KEY && model.projects && model.projects.indexOf(workspaceKey)>=0
}

export interface Tag {
    _key: string,
    name:string,
    description:string,
    tagIndex:number // Index into tag color and icon list
    imageUrl:string // url to an image
}

export interface TagGroup {
    _key: string,
    name:string,
    description:string,
    modelKeys:string[]|undefined,   // the models that use this tag group

    tags:Tag[],
    deleted?:boolean
}

export interface EmissionFactor {
    _key:string,
    scope: CARBON_SCOPE,
    type: EMISSION_FACTOR_TYPE,
    name:string,
    description:string,
    value:number,
    density:number,
    energyDensity:number,
    energyFactor:number
}

const isNonNegativeNumber = (value:any):value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0

// checks a factor's details against the organisation's other factors; returns the problem, if any
export const validateEmissionFactor = (factor:Partial<EmissionFactor>, existing:EmissionFactor[] = []):string|undefined => {
    if (!factor || typeof factor !== 'object' || !factor._key) return 'The emission factor has no key'
    const name = typeof factor.name === 'string' ? factor.name.trim() : ''
    if (name.length < EMISSION_FACTOR_NAME_MIN || name.length > EMISSION_FACTOR_NAME_MAX) return `Name must be between ${EMISSION_FACTOR_NAME_MIN} and ${EMISSION_FACTOR_NAME_MAX} characters`
    if (factor.description !== undefined && factor.description !== null && typeof factor.description !== 'string') return 'Description must be text'
    if ((factor.description || '').length > EMISSION_FACTOR_DESCRIPTION_MAX) return `Description must be ${EMISSION_FACTOR_DESCRIPTION_MAX} characters or fewer`
    if (!Object.values(CARBON_SCOPE).includes(factor.scope as CARBON_SCOPE)) return 'Choose a scope'
    if (!Object.values(EMISSION_FACTOR_TYPE).includes(factor.type as EMISSION_FACTOR_TYPE)) return 'Choose a type'
    if (!isNonNegativeNumber(factor.value) || factor.value > EMISSION_FACTOR_VALUE_MAX) return `The factor must be a number between 0 and ${EMISSION_FACTOR_VALUE_MAX}`
    for (const field of ['density', 'energyDensity', 'energyFactor'] as const) {
        const value = factor[field]
        if (value !== undefined && value !== null && !isNonNegativeNumber(value)) return 'Density, energy density and emissions per MJ must be 0 or more'
    }
    if (existing.some(ef => ef._key !== factor._key && (ef.name || '').trim().toLowerCase() === name.toLowerCase())) return `There is already an emission factor called ${name}`
    return undefined
}

export interface ModelPreferences {
    ignoreFunctions: FUNCTIONS[]
}

export interface NodeGroupModelInsights {
    groupKey:string                         // references some group, eg. information asset or tag group
    hours:number                            // total of info state hours
    numberProcessed:number                  // takes max number procssed for the states
    allocatedCostsByType:IndexedValues      // allocated costs indexed by super type
    allocatedCost:number                    // sum of allocatedCostsByType for convenience
    numberOfStates:number                   // number of info nodes/states
    value:number                            // takes highest value info node/state
    // for an information asset, how it is used across the workspace and the value derived from that (see
    // information-value.ts). The same in every model the asset is in
    assetValue?:AssetValue
}

// how an information asset is used, across all of its states, and the value derived from it
export interface AssetValue {
    consumers:number            // actions and outcomes using any of its states, not counting the work moving it from one state to another
    consumerModels:number       // models other than its own that use it
    externalConsumers:number    // external organisations performing the actions using it
    outcomesReached:number      // outcomes downstream of any of its states
    valuePerItem:number         // the highest value per item of any of its states
    revenuePerItem:number       // the highest revenue per item of any of its states
    derivedValue:number         // 0 to 100 across the assets of the organisation
}

export const createNodeGroupModelInsights = (key:string) => {
    return {
        groupKey:key,
        hours:0,
        numberProcessed:0,
        allocatedCostsByType:createIndexedValues(),
        allocatedCost:0,
        numberOfStates:0,
        value:0
    } as NodeGroupModelInsights
}

export const addNodeGroupModelInsights = (from:NodeGroupModelInsights, to:NodeGroupModelInsights) => {
    to.hours += from.hours
    to.numberProcessed += from.numberProcessed
    addIndexedValues(from.allocatedCostsByType, to.allocatedCostsByType)
    to.allocatedCost += from.allocatedCost
    to.numberOfStates += from.numberOfStates
    to.value += from.value
}

/**
 * Adds one model's figures for an information asset into the asset's total. The same items pass through every state
 * of an asset, in whichever model, and value flows back along the chain to the earlier states, so the items and value
 * are the highest of any model, not their sum. The hours and costs are separate work in each model, so they add up.
 */
export const addAssetInsights = (from:NodeGroupModelInsights, to:NodeGroupModelInsights) => {
    to.hours += from.hours
    to.numberProcessed = Math.max(to.numberProcessed, from.numberProcessed)
    addIndexedValues(from.allocatedCostsByType, to.allocatedCostsByType)
    to.allocatedCost = to.allocatedCostsByType.total
    to.numberOfStates += from.numberOfStates
    to.value = Math.max(to.value, from.value)
    if (from.assetValue) to.assetValue = from.assetValue
}

// how sensitive an information asset is
export enum ASSET_CLASSIFICATION {
    PUBLIC = 'public',
    INTERNAL = 'internal',
    CONFIDENTIAL = 'confidential',
    PERSONAL = 'personal',          // about people, so privacy law applies
}
export const ASSET_CLASSIFICATION_LABELS:Record<ASSET_CLASSIFICATION, string> = {
    [ASSET_CLASSIFICATION.PUBLIC]: 'Public',
    [ASSET_CLASSIFICATION.INTERNAL]: 'Internal',
    [ASSET_CLASSIFICATION.CONFIDENTIAL]: 'Confidential',
    [ASSET_CLASSIFICATION.PERSONAL]: 'Personal',
}
// the classifications that put an asset at risk when any of its states is kept outside a controlled system
export const SENSITIVE_CLASSIFICATIONS = [ASSET_CLASSIFICATION.CONFIDENTIAL, ASSET_CLASSIFICATION.PERSONAL]

// an information asset: the information itself, such as a customer order, which information nodes are states of. With
// the register's details: who owns it, how sensitive it is, and the system holding its authoritative copy
export interface InformationAsset {
    _key:string,
    name:string,
    description:string
    ownerKey?:string|null                       // the role accountable for it, in the organisation model
    classification?:ASSET_CLASSIFICATION|null
    systemOfRecordKey?:string|null              // the system holding its authoritative copy, in the organisation model
}

export const INFORMATION_ASSET_NAME_MIN = 3
export const INFORMATION_ASSET_NAME_MAX = 40
export const INFORMATION_ASSET_DESCRIPTION_MAX = 500
const NODE_KEY_MAX = 100

// checks an asset's details against the organisation's other assets; returns the problem, if any
export const validateInformationAsset = (asset:Partial<InformationAsset>, existing:InformationAsset[] = []):string|undefined => {
    const name = (asset?.name || '').trim()
    if (name.length < INFORMATION_ASSET_NAME_MIN || name.length > INFORMATION_ASSET_NAME_MAX) return `Name must be between ${INFORMATION_ASSET_NAME_MIN} and ${INFORMATION_ASSET_NAME_MAX} characters`
    if ((asset.description || '').length > INFORMATION_ASSET_DESCRIPTION_MAX) return `Description must be ${INFORMATION_ASSET_DESCRIPTION_MAX} characters or fewer`
    if (existing.some(a => a._key !== asset._key && (a.name || '').trim().toLowerCase() === name.toLowerCase())) return `There is already an information asset called ${name}`
    if (asset.classification && !Object.values(ASSET_CLASSIFICATION).includes(asset.classification)) return 'Classification must be public, internal, confidential or personal'
    if ([asset.ownerKey, asset.systemOfRecordKey].some(k => k !== undefined && k !== null && (typeof k !== 'string' || k.length > NODE_KEY_MAX))) return 'The owner or system of record is not a node in the organisation'
    return undefined
}

// an asset's details as saved, from what was sent: text trimmed, and, when it changes an existing asset, a detail not
// sent kept as it was (an empty one clears it)
export const cleanInformationAsset = (data:Partial<InformationAsset>, existing?:InformationAsset):InformationAsset => {
    const pick = <K extends 'ownerKey'|'classification'|'systemOfRecordKey'>(k:K) => (data[k] !== undefined ? data[k] || null : existing?.[k] ?? null) as InformationAsset[K]
    return {
        _key: existing?._key || data._key || '',
        name: data.name ? data.name.trim() : existing?.name || '',
        description: data.description !== undefined ? (data.description || '').trim() : existing?.description || '',
        ownerKey: pick('ownerKey'),
        classification: pick('classification'),
        systemOfRecordKey: pick('systemOfRecordKey'),
    }
}

export interface SectionComment {
    comment:string
    section:INSIGHTS_SECTION
}

export interface InsightsFilter {
    _key:string
    private:boolean,        // whether filter is private to owner
    owner?:string            // user key of owner
    name?:string
    description?:string
    workspaceKey:string,
    comparison:boolean,        // whether comparing a project to current state
    modelFunctions:MODEL_FUNCTION[],
    modelKeys:string[],
    modelTags:string[],
    nodeTags:string[],
    nodeType:NODE_TYPE|undefined,
    nodeKeys:string[],
    informationAssetKeys:string[],
    sections:INSIGHTS_SECTION[]
    fields:INSIGHTS_FIELD[]
    comments:SectionComment[]
}

export const cloneInsightsfilter = (filter:InsightsFilter, workspaceKey:string) => {
    return {...filter, _key:v4(), workspaceKey}
}

export const createInsightsFilter = (workspaceKey:string=CURRENT_STATE_PROJECT_KEY, userKey?:string):InsightsFilter => {
    return {
        _key:'',
        owner:userKey,
        private:true,
        name:'',
        description:'',
        workspaceKey:workspaceKey,
        comparison:false,
        modelFunctions:[],
        modelKeys:[],
        modelTags:[],
        nodeTags:[],
        nodeType:undefined,
        nodeKeys:[],
        informationAssetKeys:[],
        sections:[],
        fields:[],
        comments:[]
    } as InsightsFilter
}

// whether the user may change or delete a saved filter: its owner, or an organisation admin for a shared (public) one
export const canChangeInsightsFilter = (filter:InsightsFilter, userKey:string, organisationRole:USER_ROLE, isSystemAdmin:boolean=false) => {
    if (!filter?._key) return true
    if (isSystemAdmin || filter.owner === userKey) return true
    return !filter.private && organisationRole === USER_ROLE.ADMIN
}

// copies the submitted settings onto a saved filter. Settings that can be switched off or cleared are copied whenever
// they are sent (null clears), so turning off comparison or clearing the node type is saved too
export const updateFilterData = (filterData:InsightsFilter, existingFilter:InsightsFilter) => {
    if (filterData.name) existingFilter.name = filterData.name
    if (filterData.description !== undefined) existingFilter.description = filterData.description || ''
    if (filterData.private !== undefined) existingFilter.private = filterData.private
    if (filterData.workspaceKey) existingFilter.workspaceKey = filterData.workspaceKey
    if (filterData.comparison !== undefined) existingFilter.comparison = !!filterData.comparison
    if (filterData.modelKeys) existingFilter.modelKeys = filterData.modelKeys
    if (filterData.modelFunctions) existingFilter.modelFunctions = filterData.modelFunctions
    if (filterData.modelTags) existingFilter.modelTags = filterData.modelTags
    if (filterData.nodeTags) existingFilter.nodeTags = filterData.nodeTags
    if (filterData.nodeType !== undefined) existingFilter.nodeType = filterData.nodeType || undefined
    if (filterData.nodeKeys) existingFilter.nodeKeys = filterData.nodeKeys
    if (filterData.informationAssetKeys) existingFilter.informationAssetKeys = filterData.informationAssetKeys
    if (filterData.sections) existingFilter.sections = filterData.sections
    if (filterData.fields) existingFilter.fields = filterData.fields
    if (filterData.comments) existingFilter.comments = filterData.comments
}

// the models a filter picks out by key, function or tag (any of them); all models when it picks none. Filters saved
// before a setting existed don't have it
export const getModelsForFilter = (filter:InsightsFilter, organisation:Organisation, project?:Project):ModelDetails[] => {
    const modelKeys = filter.modelKeys || [], modelFunctions = filter.modelFunctions || [], modelTags = filter.modelTags || []
    if (modelKeys.length === 0 && modelFunctions.length === 0 && modelTags.length === 0) return getUnsortedModelList(organisation, project)
    return getUnsortedModelList(organisation, project).filter(m => {
        if (modelKeys.indexOf(m._key) >= 0) return true
        if (modelFunctions.indexOf(m.function) >= 0) return true
        return (m.tagKeys || []).some(value => modelTags.includes(value))
    })
}

export interface Organisation {
    _key:string,
    name:string,
    industry: INDUSTRY,
    currencyCode:string,
    isPartner: boolean,
    stage: ORGANISATION_STAGE,      // Stage of org in process. used by crm to understand what contact is required
    isExemptFromPayment:boolean,
    notCustomer:boolean             // whether the organisation is a customer or an internal organisation used for testing/demo
    invoiceReference: string,
    billingEmail:string,
    contactId:string    // contact id from financial system
    CRMId:string        // ID of contact in CRM system
    CRMDealId:string    // ID of the deal in CRM system
    CRMExpired?:boolean // whether the CRM deal has been moved to the expired stage, as the organisation has expired
    model:ModelDetails, // organisation model and insights
    lastVersionUpdate: string, // records when the data was brought up to date
    defaultCapacityHours?: number, // the hours a year one full-time person works, for people and roles whose capacity isn't set
    insightsVersion?: string,  // the calculation its insights were last all worked out with (set by the server)
    createdDate:Date,
    lastAccess:Date,
    endDate?: Date,
    partnerKey?: string,
    state: ORGANISATION_STATE,
    models:ModelDetails[],
    projects:Project[],
    tagGroups:TagGroup[],
    modelTags:TagGroup,
    users:AccessRights[],
    modelPreferences:ModelPreferences,
    emissionFactors:EmissionFactor[],
    informationAssets:InformationAsset[],
    insightsFilters:InsightsFilter[],
    backup?:Backup           // can have one backup of whole org

    // following only populated when downloaded
    // current users role and status
    role:USER_ROLE|undefined
    numUsers:number,
    numAdmins:number,
    numNodes:number,
    partnerName?:string // the partner that setup this org
}

export const createModelTags = ():TagGroup => {
    return {
        _key:MODEL_TAG_GROUP_KEY,
        name:"Model tags",
        description:"Tags used to categorise models",
        tags:[],
        modelKeys:undefined,
        deleted:false
    }
}

export const getOrganisationState = (organisation:Organisation, date?:Date) => {
    const dateToCompare = date || new Date()

    // a fortnight from the date being checked (it was from today's month with that date's day)
    const nearly = new Date(dateToCompare)
    nearly.setDate(nearly.getDate() + ORGANISATION_ALMOST_EXPIRED)

    if (organisation.endDate && dateToCompare > new Date(organisation.endDate)) return ORGANISATION_STATE.EXPIRED
    else if (organisation.endDate && nearly > new Date(organisation.endDate)) return ORGANISATION_STATE.NEAR_EXPIRY
    else return ORGANISATION_STATE.ACTIVE
}

export const getCurrentStateModelKeysForProject = (organisation:Organisation, projectKey?:string) => {
    return (projectKey && projectKey !== CURRENT_STATE_PROJECT_KEY)?(organisation.models || []).reduce((a:string[], m:ModelDetails) => {
            if (!m.projects || m.projects.indexOf(projectKey) < 0)
                a.push(m._key)

            return a
        }, []):[]
}

export const findTagForOrganisation = (organisation:Organisation, tagKey:string):{group:TagGroup, tag:Tag}|null => {
    for (const tg of organisation?.tagGroups || []) {
        const tag = tg.tags?.find(t => t._key === tagKey)
        if (tag) return {group:tg, tag}
    }
    return null
}

export const getTagGroupsForModel = (organisation:Organisation, modelKey:string) => {
    if (modelKey === ORGANISATION_MODEL_KEY) {
        return organisation && [organisation.modelTags]
    }else return organisation && organisation.tagGroups?organisation.tagGroups.filter(tg => !tg.modelKeys || tg.modelKeys.length===0 || tg.modelKeys.indexOf(TAG_GROUP_ALL_MODELS)>=0 || tg.modelKeys.indexOf(modelKey)>=0):[]
}

export const createOrganisationModel = () => {
    return {
        _key: ORGANISATION_MODEL_KEY,
        projectKey:CURRENT_STATE_PROJECT_KEY,
        name:'Organisation model',
        description:'Contains all organisation level nodes and model joins',
        insights: []
    } as unknown as ModelDetails
}

export const createOrganisation = ():Organisation => {
    return {
        _key: v4(),
        name: "",
        model: createOrganisationModel(),
        createdDate: new Date(),
        lastAccess: new Date(),
        isPartner:false,
        notCustomer:false,
        stage:ORGANISATION_STAGE.REGISTERED,
        isExemptFromPayment:false,
        // a typical full-time year, for people and roles whose capacity isn't set
        defaultCapacityHours: SUGGESTED_FULL_TIME_HOURS,
        models: [],
        projects: [],
        tagGroups: [],
        modelTags:createModelTags(),
        users: [],
        emissionFactors: [],
        informationAssets: [],
        modelPreferences: { ignoreFunctions: [] }
    } as unknown as Organisation
}

export const resetModelInsightsCosts = (insights:ModelInsights) => {
    Object.keys(insights.superTypes).forEach(s => {
        insights.superTypes[s].allocatedCost = 0
        insights.superTypes[s].percentageModelled = 0
        insights.superTypes[s].capacity = 0
    })
    if (!insights.externalOrgs) insights.externalOrgs = createInsightTotal()
    insights.externalOrgs.allocatedCost = 0

    Object.keys(insights.actionTypes).forEach(t => {
        insights.actionTypes[t].cost = 0
    })

    insights.modelledCapacity = 0
    insights.unModelledCapacity = 0
    insights.unusedCapacity = 0
    insights.informationAssets = []
    insights.tags = []
    insights.emissions = []
    insights.costAllocations = []
    insights.allocatedCarbonByScope = createIndexedValues()
    insights.processingErrors = []
}

export const createModelInsights = (organisationKey:string, projectKey:string, modelKey:string):ModelInsights => {
    const i = {
        _key: v4(),
        organisationKey,
        projectKey,
        modelKey,
        updatedDate:new Date(),
        superTypes: {},
        externalOrgs: createInsightTotal(),
        emissions:[],
        costAllocations:[],
        actionTypes: {},
        allocatedCarbonByScope: createIndexedValues(),
        infoSuppliedBySystems: 0,
        roleWithHighestActions: "",
        systemWithHighestActions: "",
        modelledCapacity: 0,
        unModelledCapacity: 0,
        unusedCapacity: 0,
        outputValue: 0,
        outputRevenue: 0,
        frictionCost: 0,
        frictionCarbon: 0,
        frictionTime: 0,
        exceptionCost: 0,
        exceptionCarbon: 0,
        exceptionTime: 0,
        exceptionActions: 0,
        wasteCost: 0,
        wasteTime: 0,
        numOutputs: 0,
        numModels: 0,
        numOutputsProcessed: 0,
        numInputsProcessed: 0,
        numInputsExternal: 0,
        informationAssets: [],
        tags: [],
        highestValues: {},
        processingErrors: []
    } as ModelInsights

    enumToArray(NODE_SUPERTYPE).forEach(s => {
        i.superTypes[s.value] = createInsightTotal()
    })
    enumToArray(ACTION_TYPE).forEach(t => {
        i.actionTypes[t.value] = createActionType()
    })
    return i
}

export const roundModelInsights = (insights:ModelInsights) => {
    insights.outputValue = Math.round(insights.outputValue)
    insights.outputRevenue = Math.round(insights.outputRevenue)
    insights.frictionCost = Math.round(insights.frictionCost)
    insights.frictionCarbon = Math.round(insights.frictionCarbon)
    insights.frictionTime = Math.round(insights.frictionTime)
    insights.exceptionCost = Math.round(insights.exceptionCost||0)
    insights.exceptionCarbon = Math.round(insights.exceptionCarbon||0)
    insights.exceptionTime = Math.round(insights.exceptionTime||0)
    insights.wasteCost = Math.round(insights.wasteCost||0)
    insights.wasteTime = Math.round(insights.wasteTime||0)

    insights.numOutputsProcessed = Math.round(insights.numOutputsProcessed)
    insights.modelledCapacity = Math.round(insights.modelledCapacity)
    insights.unModelledCapacity = Math.round(insights.unModelledCapacity)
    insights.unusedCapacity = Math.round(insights.unusedCapacity)
    insights.numInputsProcessed = Math.round(insights.numInputsProcessed)
    insights.numOutputsProcessed = Math.round(insights.numOutputsProcessed)

    if (insights.allocatedCarbonByScope) roundIndexedValues(insights.allocatedCarbonByScope)

    Object.values<InsightTotal>(insights.superTypes).forEach(t => {
        t.hours = Math.round(t.hours)
        t.capacity = Math.round(t.capacity)
        t.allocatedCost = Math.round(t.allocatedCost)
        t.percentageModelled = Math.round(t.percentageModelled)
    })
    if (!insights.externalOrgs) insights.externalOrgs = createInsightTotal()
    insights.externalOrgs.hours = Math.round(insights.externalOrgs.hours)
    insights.externalOrgs.allocatedCost = Math.round(insights.externalOrgs.allocatedCost)

    if (insights.actionTypes) {
        Object.values<ActionType>(insights.actionTypes).forEach(t => {
            t.cost = Math.round(t.cost)
            t.hours = Math.round(t.hours)
            t.processed = Math.round(t.processed)
        })
    }
    roundAllocations(insights.emissions)
    roundAllocations(insights.costAllocations)

    if (insights.informationAssets) {
        insights.informationAssets.forEach(ia => {
            ia.hours = Math.round(ia.hours)
            roundIndexedValues(ia.allocatedCostsByType)
        })
    }
    if (insights.tags) {
        insights.tags.forEach(ia => {
            ia.hours = Math.round(ia.hours)
            roundIndexedValues(ia.allocatedCostsByType)
        })
    }
}

export const totalModelInsights = (models:ModelDetails[], workspaceKey:string, organisationKey:string, nodes?:LinqsetNode[]) => {
    const modelInsights = models.map(m => m.insights.find(i => i.projectKey === workspaceKey)).filter(i => i) as ModelInsights[]
    const modelKeys = models.map(m => m._key)
    const totals = modelInsights.reduce((total:ModelInsights, insights:ModelInsights) => {
        total.numModels++
        if (insights.actionTypes) {
            Object.keys(insights.actionTypes).forEach(k => {
                if (!total.actionTypes[k]) total.actionTypes[k] = createActionType()
                total.actionTypes[k].hours += insights.actionTypes[k].hours
                total.actionTypes[k].cost += insights.actionTypes[k].cost
                total.actionTypes[k].processed += insights.actionTypes[k].processed
                total.actionTypes[k].number += insights.actionTypes[k].number
            })
        }
        addIndexedValues(insights.allocatedCarbonByScope, total.allocatedCarbonByScope)

        total.infoSuppliedBySystems += insights.infoSuppliedBySystems
        total.modelledCapacity += insights.modelledCapacity

        if (insights.informationAssets) {
            insights.informationAssets.forEach(ia => {
                let totalIA = total.informationAssets.find(tia => tia.groupKey === ia.groupKey)
                if (!totalIA) {
                    totalIA = createNodeGroupModelInsights(ia.groupKey)
                    total.informationAssets.push(totalIA)
                }
                addAssetInsights(ia, totalIA)
            })
        }
        if (insights.tags) {
            insights.tags.forEach(t => {
                let totalTag = total.tags.find(tt => tt.groupKey === t.groupKey)
                if (!totalTag) {
                    totalTag = createNodeGroupModelInsights(t.groupKey)
                    total.tags.push(totalTag)
                }
                addNodeGroupModelInsights(t, totalTag)
            })
        }
        // don't count value twice when both the upstream model and the downstream model that created it are included
        const fromSet = getValueFromModelsInSet(insights, modelKeys)
        total.outputValue += insights.outputValue - fromSet.value
        total.outputRevenue += insights.outputRevenue - fromSet.revenue
        const contributions = insights.valueFromModels
        if (contributions) {
            const fromOutside:ModelValueContributions = {}
            Object.keys(contributions).filter(k => modelKeys.indexOf(k) < 0).forEach(k => fromOutside[k] = contributions[k])
            if (!total.valueFromModels) total.valueFromModels = {}
            addValueContributions(fromOutside, total.valueFromModels)
        }
        total.frictionCost += insights.frictionCost
        total.frictionCarbon += insights.frictionCarbon
        total.frictionTime += insights.frictionTime

        total.numOutputs += insights.numOutputs
        total.numOutputsProcessed += insights.numOutputsProcessed
        total.numInputsProcessed += insights.numInputsProcessed
        total.numInputsExternal += insights.numInputsExternal

        total.unusedCapacity += insights.unusedCapacity

        if (insights.superTypes) {
            Object.keys(insights.superTypes).forEach(k => {
                const sType = parseInt(k) as NODE_SUPERTYPE
                if (!total.superTypes[sType]) {
                    total.superTypes[sType] = createInsightTotal()

                    // if organisation node get totals from linqset
                    if (isOrganisationNode(sType) && nodes)
                        total.superTypes[k].number = nodes.filter(n => n.superType === sType).length
                }
                addInsightTotal(insights.superTypes[k], total.superTypes[k], sType)
            })
        }

        addInsightTotal(insights.externalOrgs, total.externalOrgs, NODE_SUPERTYPE.ROLE)
        return total
    }, createModelInsights(organisationKey, workspaceKey, FILTER_MODEL_KEY))

    if (nodes) {
        totals.externalOrgs.number = nodes.filter(n => n.type === NODE_TYPE.EXTERNAL_ORGANISATION).length
        Object.keys(totals.superTypes).forEach(k => {
            const sType = parseInt(k) as NODE_SUPERTYPE
            if (isOrganisationNode(sType))
                totals.superTypes[k].number = nodes.filter(n => n.superType === sType).length
        })
    }
    return totals
}

// a typical full-time working year: 40 hours a week for 46 weeks, after leave and public holidays
export const SUGGESTED_FULL_TIME_HOURS = 1840
// no one works more than every hour of the year
export const MAX_FULL_TIME_HOURS = 8760

// the hours a year a full-time person works, for people and roles whose capacity isn't set; 0 when the organisation
// has none, so their capacity is left to match their work
export const defaultCapacityHours = (organisation?: Pick<Organisation, 'defaultCapacityHours'>) => {
    const hours = Number(organisation?.defaultCapacityHours)
    return Number.isFinite(hours) && hours > 0 ? Math.min(hours, MAX_FULL_TIME_HOURS) : 0
}

export const getUnsortedModelList = (organisation:Organisation, project?:Project) => {
    // a project has the current state models (unless it's a sandbox) and its own; archived ones are left out of both
    const models:ModelDetails[] = project?(organisation.models || []).filter(m => !m.deletedDate && ((!project.sandbox && m.projectKey === CURRENT_STATE_PROJECT_KEY) || m.projectKey === project._key)):
        (organisation.models || []).filter(m => !m.deletedDate && m.projectKey === CURRENT_STATE_PROJECT_KEY)
    return models
}

/**
 * Orders models so that a model comes before any model that receives information from it (upstream first).
 * Insights processing relies on this: times flow upstream -> downstream, values are then assigned in reverse.
 * Models are linked by JOINS and USED_IN edges from an external information node (owned by the source model)
 * into the receiving model (edge.modelKey). Models caught in a cycle are appended at the end rather than dropped.
 */
export const getOrganisationModelList = (organisation:Organisation, joinsGraph:GraphData, project?:Project):ModelDetails[] => {
    const models = getUnsortedModelList(organisation, project)
    const modelKeySet = new Set(models.map(m => m._key))

    const infoNodeModel = new Map<string, string>()
    joinsGraph.nodes.filter(n => n.external).forEach(n => infoNodeModel.set(n._key, n.modelKey))

    // upstream model key -> downstream model keys
    const downstream = new Map<string, Set<string>>()
    const incoming = new Map<string, number>(models.map(m => [m._key, 0]))
    joinsGraph.edges.filter(e => e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN).forEach(e => {
        const from = infoNodeModel.get(e.sourceKey)
        const to = e.modelKey
        if (!from || from === to || !modelKeySet.has(from) || !modelKeySet.has(to)) return
        const targets = downstream.get(from) || new Set<string>()
        downstream.set(from, targets)
        if (!targets.has(to)) {
            targets.add(to)
            incoming.set(to, (incoming.get(to) || 0) + 1)
        }
    })

    // topological sort, keeping the original order where there is no dependency
    const ordered:ModelDetails[] = []
    const ready = models.filter(m => incoming.get(m._key) === 0)
    let next = ready.shift()
    while (next) {
        ordered.push(next)
        downstream.get(next._key)?.forEach(k => {
            const remaining = (incoming.get(k) || 0) - 1
            incoming.set(k, remaining)
            const dm = models.find(m => m._key === k)
            if (remaining === 0 && dm) ready.push(dm)
        })
        next = ready.shift()
    }

    if (ordered.length !== models.length) {
        console.error("Models in organisation " + organisation.name + " have a circular dependency. Values may not propagate fully between them.")
        models.filter(m => ordered.indexOf(m) < 0).forEach(m => ordered.push(m))
    }
    return ordered
}

export const addValueContributions = (from:ModelValueContributions|undefined, to:ModelValueContributions) => {
    if (!from) return
    Object.keys(from).forEach(k => {
        if (!to[k]) to[k] = {value:0, revenue:0}
        to[k].value += from[k].value
        to[k].revenue += from[k].revenue
    })
}

/**
 * An upstream model's outputValue/outputRevenue includes value it enabled downstream. When totalling several models
 * that value would otherwise be counted twice, so this returns the part that came from models in the same set.
 */
export const getValueFromModelsInSet = (insights:ModelInsights, modelKeys:string[]) => {
    const total = {value:0, revenue:0}
    const contributions = insights?.valueFromModels
    if (!contributions) return total
    Object.keys(contributions).filter(k => modelKeys.indexOf(k) >= 0).forEach(k => {
        total.value += contributions[k].value
        total.revenue += contributions[k].revenue
    })
    return total
}

// used to describe billing information
export interface BillingAmount {
    number:number,
    amount:number
}

export const createBillingAmount = (number?:number, amount?:number):BillingAmount => {
    return {number:number||0, amount:amount||0} as BillingAmount
}

export const addToBillingAmount = (billingAmount:BillingAmount, number:number, amount:number) => {
    billingAmount.number += number
    billingAmount.amount += amount
}

export const addBillingAmountToBillingAmount = (from:BillingAmount, to:BillingAmount) => {
    to.number += from.number
    to.amount += from.amount
}

export enum BILLING_STATUS {
    CREATED,
    INVOICED,
    PAID,
    EXEMPT,
    EXPIRED_ORGANISATION,
    MISSING_USAGE,
    INVOICING_ERROR,
    PAYMENT_ERROR,
    OVERDUE,
    PREDICTION
}

export const getBillingStatusDescription = (status:BILLING_STATUS) => {
    switch(status) {
        case BILLING_STATUS.CREATED:
            return "Created"
        case BILLING_STATUS.INVOICED:
            return "Invoiced"
        case BILLING_STATUS.EXEMPT:
            return "Exempt"
        case BILLING_STATUS.EXPIRED_ORGANISATION:
            return "Expired organisation"
        case BILLING_STATUS.PAID:
            return "Paid"
        case BILLING_STATUS.MISSING_USAGE:
            return "Missing usage"
        case BILLING_STATUS.PAYMENT_ERROR:
            return "Payment error"
        case BILLING_STATUS.INVOICING_ERROR:
            return "Invoicing error"
        case BILLING_STATUS.OVERDUE:
            return "Overdue"
        case BILLING_STATUS.PREDICTION:
            return "Predicted"

    }
}

export const getRunIdForDate = (date:Date) => {
    return (date.getFullYear() * 100) + (date.getMonth())
}

// the month a billing run is for. Run IDs hold the month the run happened (0 for January) and a run bills the month
// before, so January's run is for December (which had no name)
export const getMonthForRunId = (runId:number) => {
    const month = runId % 100
    return SHORT_MONTHS[(month + 11) % 12]
}

// is used to record a point in time for the organisation. Special records are created by billing runs at the end of each month. these have
// a runId made up ofr year*100+month
export interface OrganisationBilling {
    _key:string
    organisationKey:string,
    organisationName:string,
    invoiceReference: string,
    currencyCode:string,
    createdDate:Date,              // date created
    runId:number,           // if was created in a billng run this is set
    status:BILLING_STATUS,  // whether paid or not
    amount:number,          // the amount ot pay based on usage
    invoiceNumber:string        // invoice number from finance system
    invoiceId:string        // invoice ID from finance system
    contactId:string        // payment system contact or customer ID
    dateInvoiced:Date       // date invoiced
    paidDate:Date           // date invoice was paid

    nodes:BillingAmount,           // number and amount for nodes
    projects:BillingAmount,        // number and amount for projects
    users:BillingAmount,           // number and amount for users

    error:string|undefined                // if payment results in an error.
}

export const getCurrency = (code:string) => {
    return Currencies.find(c => c.code === code) || Currencies[0]
}

export const createOrganisationBillingRecord = (organisation:Organisation, createdDate:Date, billingDate:Date, runId:number, ma?:ModelAnalytics):OrganisationBilling => {
    let status = BILLING_STATUS.CREATED

    if (getOrganisationState(organisation, billingDate) == ORGANISATION_STATE.EXPIRED) status = BILLING_STATUS.EXPIRED_ORGANISATION
    else if (organisation.isExemptFromPayment) status = BILLING_STATUS.EXEMPT
    else if (!ma) status = BILLING_STATUS.MISSING_USAGE

    const record = {
        _key:v4(),
        organisationKey:organisation._key,
        organisationName:organisation.name,
        currencyCode:organisation.currencyCode||DEFAULT_CURRENCY,
        createdDate,
        runId,
        status,
        invoiceReference:organisation.invoiceReference,
        amount:0,
        nodes:ma?createBillingAmount(ma.nodes + ma.projectNodes + ma.backupNodes, 0):createBillingAmount(),
        projects:ma?createBillingAmount(ma.projects, 0):createBillingAmount(),
        users:ma?createBillingAmount(ma.users, 0):createBillingAmount()
    } as OrganisationBilling

    calculateCosts(record)
    return record
}

export const calculateCosts = (record:OrganisationBilling) => {

    const costs = calculateRawCosts(record.nodes.number, record.users.number, record.projects.number, getCurrency(record.currencyCode))

    record.nodes.amount = costs.nodeAmount
    record.projects.amount = costs.projectAmount
    record.users.amount = costs.userAmount
    record.amount = costs.amount
}

export const calculateRawCostsForOrganisation = (organisation:Organisation) => {
    return calculateRawCosts(organisation.numNodes||0, organisation.numUsers||0, billableProjectCount(organisation.projects), getCurrency(organisation.currencyCode))
}

export const calculateRawCosts = (nodes:number, users:number, projects:number, currency:Currency) => {
    const nodeAmount = roundTwoDP((nodes-FREE_NODES>0?nodes-FREE_NODES:0)*currency.node)
    const projectAmount = roundTwoDP((projects-FREE_PROJECTS>0?projects-FREE_PROJECTS:0)*currency.project)
    const userAmount = roundTwoDP((users-FREE_USERS>0?users-FREE_USERS:0)*currency.user)

    return {amount: roundTwoDP(nodeAmount + projectAmount + userAmount), nodeAmount, projectAmount, userAmount}
}

export interface BillingRunSummary {
    runId:number,
    nodes:BillingAmount,
    projects:BillingAmount,
    users:BillingAmount,
    organisations:BillingAmount,
    states:any
}

export const createBillingRunSummary = (runId:number) => {
    return {
        runId,
        nodes:createBillingAmount(),
        projects:createBillingAmount(),
        users:createBillingAmount(),
        organisations:createBillingAmount(),
        states:{}
    }
}